/**
 * Country-based display pricing for Power TOEFL.
 *
 * These are the amounts shown to the user and the amounts we mirror into
 * Dodo Payments localized prices (see scripts/sync-dodo-localized-prices.mjs).
 *
 * NOTE: `monthly` / `yearly` are in MAJOR currency units (what a human reads).
 * Conversion into the smallest currency unit happens in the sync script.
 */

export type CountryPrice = {
  country: string;
  currency: string;
  monthly: number;
  yearly: number;
  /** Locale-formatted display strings */
  monthlyLabel: string;
  yearlyLabel: string;
};

/** Currencies with no minor unit in display. Dodo's IDR API is 2-decimal — see scripts/sync-dodo-localized-prices.mjs. */
export const ZERO_DECIMAL_CURRENCIES = ['KRW', 'JPY', 'VND', 'IDR', 'MMK'];

export const USD_DEFAULT: CountryPrice = {
  country: 'US',
  currency: 'USD',
  monthly: 14.99,
  yearly: 99,
  monthlyLabel: '$14.99',
  yearlyLabel: '$99',
};

export const COUNTRY_PRICING: Record<string, CountryPrice> = {
  KR: { country: 'KR', currency: 'KRW', monthly: 19900, yearly: 199000, monthlyLabel: '₩19,900', yearlyLabel: '₩199,000' },
  JP: { country: 'JP', currency: 'JPY', monthly: 2480, yearly: 24800, monthlyLabel: '¥2,480', yearlyLabel: '¥24,800' },
  VN: { country: 'VN', currency: 'VND', monthly: 199000, yearly: 1990000, monthlyLabel: '199,000₫', yearlyLabel: '1,990,000₫' },
  TH: { country: 'TH', currency: 'THB', monthly: 349, yearly: 3490, monthlyLabel: '฿349', yearlyLabel: '฿3,490' },
  ID: { country: 'ID', currency: 'IDR', monthly: 169000, yearly: 1690000, monthlyLabel: 'Rp169.000', yearlyLabel: 'Rp1.690.000' },
  MY: { country: 'MY', currency: 'MYR', monthly: 39, yearly: 399, monthlyLabel: 'RM39', yearlyLabel: 'RM399' },
  DE: { country: 'DE', currency: 'EUR', monthly: 12.99, yearly: 129, monthlyLabel: '€12,99', yearlyLabel: '€129' },
  ES: { country: 'ES', currency: 'EUR', monthly: 12.99, yearly: 129, monthlyLabel: '€12,99', yearlyLabel: '€129' },
  FR: { country: 'FR', currency: 'EUR', monthly: 12.99, yearly: 129, monthlyLabel: '€12,99', yearlyLabel: '€129' },
  SA: { country: 'SA', currency: 'SAR', monthly: 49, yearly: 499, monthlyLabel: 'ر.س49', yearlyLabel: 'ر.س499' },
  BR: { country: 'BR', currency: 'BRL', monthly: 39.9, yearly: 399, monthlyLabel: 'R$39,90', yearlyLabel: 'R$399' },
  TW: { country: 'TW', currency: 'TWD', monthly: 499, yearly: 4990, monthlyLabel: 'NT$499', yearlyLabel: 'NT$4,990' },
  CN: { country: 'CN', currency: 'CNY', monthly: 128, yearly: 1280, monthlyLabel: '￥128', yearlyLabel: '￥1,280' },
};

/** Map UI language → default country used for display pricing. */
const LANG_TO_COUNTRY: Record<string, string> = {
  ko: 'KR',
  ja: 'JP',
  vi: 'VN',
  th: 'TH',
  id: 'ID',
  ms: 'MY',
  my: 'US', // MMK is not supported for localized checkout prices → charged in USD
  de: 'DE',
  es: 'ES',
  fr: 'FR',
  ar: 'SA',
  'pt-BR': 'BR',
  'zh-TW': 'TW',
  'zh-CN': 'CN',
  en: 'US',
};

export const GEO_COUNTRY_KEY = 'pt_geo_country';

export function readStoredGeoCountry(): string | null {
  try {
    const raw = localStorage.getItem(GEO_COUNTRY_KEY);
    if (!raw) return null;
    const cc = raw.toUpperCase();
    return /^[A-Z]{2}$/.test(cc) && cc !== 'XX' ? cc : null;
  } catch {
    return null;
  }
}

/** Checkout market follows UI language, not IP (VPN users). */
export function resolveCheckoutCountry(lang?: string | null): string {
  return getCountryPricing(lang).country;
}

export function getCheckoutPricing(lang?: string | null): CountryPrice {
  return getCountryPricing(lang);
}

export function getCountryPricing(countryOrLang?: string | null): CountryPrice {
  if (!countryOrLang) return USD_DEFAULT;
  const direct = COUNTRY_PRICING[countryOrLang.toUpperCase()];
  if (direct) return direct;
  const mapped = LANG_TO_COUNTRY[countryOrLang];
  if (mapped && COUNTRY_PRICING[mapped]) return COUNTRY_PRICING[mapped];
  return USD_DEFAULT;
}

/** Convert a major-unit amount to the smallest currency unit for a currency. */
export const toSmallestUnit = (amount: number, currency: string): number =>
  ZERO_DECIMAL_CURRENCIES.includes(currency.toUpperCase())
    ? Math.round(amount)
    : Math.round(amount * 100);
